import VerificationProvider from '../../enums/VerificationProvider';
import {handleThreeDSecureRequired} from '../api/pollerHandlers/statusHandlers/handleThreeDSecureRequired';
import {orderStatusPromisePoller} from '../api/pollers/pollers';
import {redirect} from '../api/redirect';
import {getErrorRedirect, getSuccessRedirect} from '../selectors/OrderProcessingSelectors';
import {getSettings} from '../selectors/selectors';
import getThreeDSVerificationProvider from '../selectors/ThreeDSSelectors/getThreeDSVerificationProvider';
import store from '../store/store';
import doMetric from '../tracking/bigQueryMetricRecorder';
import Track from '../utils/tracking';
import WeChatHandler from './payment-handlers/wechat';
import Adyen from './payment-methods/adyen';
import AdyenV2Processor from './payment-methods/AdyenV2Processor/AdyenV2Processor';
import AfterPay from './payment-methods/afterpay';
import AlipayPlusProcessor from './payment-methods/AlipayPlusProcessor/AlipayPlusProcessor';
import AmazonPayProcessor from './payment-methods/AmazonPayProcessor/AmazonPayProcessor';
import AtomeProcessor from './payment-methods/AtomeProcessor/AtomeProcessor';
import HummProcessor from './payment-methods/HummProcessor/HummProcessor';
import FlexPayProcessor from './payment-methods/FlexPayProcessor/FlexPayProcessor';
import FrasersPlusProcessor from './payment-methods/FrasersPlusProcessor/FrasersPlusProcessor';
import Kcp from './payment-methods/kcp';
import Klarna from './payment-methods/klarna';
import LaybuyProcessor from './payment-methods/LaybuyProcessor/LaybuyProcessor';
import OpenpayProcessor from './payment-methods/OpenpayProcessor/OpenpayProcessor';
import OpttyProcessor from './payment-methods/OpttyProcessor/OpttyProcessor';
import Paypal from './payment-methods/paypal';
import PayVyneProcessor from './payment-methods/PayVyneProcessor/PayVyneProcessor';
import SezzleProcessor from './payment-methods/SezzleProcessor/SezzleProcessor';
import SplititProcessor from './payment-methods/SplititProcessor/SplititProcessor';
import TrustlyProcessor from './payment-methods/TrustlyProcessor/TrustlyProcessor';
import Wechat from './payment-methods/wechat';
import WeChatApp from './payment-methods/wechatApp';
import Worldpay from './payment-methods/worldpay';
import ZipPayProcessor from './payment-methods/ZipPayProcessor/ZipPayProcessor';

const hostedPaymentMethods = [Paypal, Adyen, Klarna, WeChatApp, Wechat, AfterPay, Kcp, Worldpay,
  LaybuyProcessor, SplititProcessor, OpenpayProcessor, OpttyProcessor, ZipPayProcessor, SezzleProcessor, TrustlyProcessor,
  PayVyneProcessor, HummProcessor, FlexPayProcessor, FrasersPlusProcessor, AtomeProcessor, AlipayPlusProcessor, AdyenV2Processor,
  AmazonPayProcessor];

function getHostedPaymentMethodReturningFromHostedPage(queryParams) {
  return hostedPaymentMethods.find(element => element.isReturningFromHostedPage(queryParams));
}

function getHostedPaymentMethodTokenReceived(status) {
  return hostedPaymentMethods.find(element => element.isTokenReceived(status));
}

function callApiIfReturningFromHostedPaymentPage(queryParams) {
  const hostedPaymentMethod = getHostedPaymentMethodReturningFromHostedPage(queryParams);
  if (hostedPaymentMethod) {
    return hostedPaymentMethod.returnedFromHostedPage(queryParams);
  }
  return Promise.resolve();
}

function setOrderStatusPromisePollerTimeout(duration, qrCode, setQrCode) {
  setTimeout(() => orderStatusPromisePoller.runPoller()
    // eslint-disable-next-line no-use-before-define
    .then(res => processResponse(res, qrCode, setQrCode))
    .catch(() => {
    }), duration);
}

function processResponse(response, qrCode, setQrCode) {
  const {body: orderJSON} = response;
  const {orderStatus: status, paymentAccessUrl} = orderJSON;
  const state = store.getState();
  const settings = getSettings(state);
  
  if (settings?.enable_bq_logging_for_order_status) {
    try {
      doMetric('orderStatus', [{name: 'paymentHelperOrderStatus', value: status}]);
    } catch (errorMsg) {
      Track.track(errorMsg);
    }
  }
  
  if (status === 'SET_ORDER_PAYMENT_REQUESTED' && orderJSON?.paymentMethod === 'GOOGLEPAY') {
    setOrderStatusPromisePollerTimeout(1000, qrCode, setQrCode);
    
    return Promise.resolve('3DS_REQUIRED');
  }
  
  if (status === 'HOSTED_PAYMENT_CURRENTLY_UNAVAILABLE') {
    const {paymentMethod} = orderJSON;
    const error = orderJSON.errorStatus;
    if (paymentMethod === 'PAYVYNE') {
      if (!error) {
        const url = '/checkout/?error=null';
        return Promise.resolve(url);
      }
      const errorForUrl = error.toLowerCase();
      const url = `/checkout/?error=${errorForUrl}`;
      return Promise.resolve(url);
    }
  }
  
  if (status === 'WECHAT_USER_WAITING') {
    if (!qrCode.display) {
      setQrCode({
        display: true,
        data: {
          url: orderJSON.paymentAccessUrl,
        },
        provider: 'WECHAT',
      });
      setOrderStatusPromisePollerTimeout(5000, qrCode, setQrCode);
    } else {
      setOrderStatusPromisePollerTimeout(3500, qrCode, setQrCode);
    }

    return Promise.resolve('QR_CODE');
  }
  
  if (status === 'MODE_PAYMENT_ATTRIBUTES_RECEIVED') {
    if (!qrCode.display) {
      setQrCode({
        display: true,
        data: orderJSON.paymentMethodAttributes,
        provider: 'MODE',
      });
      setOrderStatusPromisePollerTimeout(5000, qrCode, setQrCode);
    } else {
      setOrderStatusPromisePollerTimeout(3500, qrCode, setQrCode);
    }
    
    return Promise.resolve('QR_CODE');
  }
  
  if (status === 'THREE_DS_CONFIRMATION_REQUIRED') {
    const threeDsStatus = handleThreeDSecureRequired(orderJSON);
    
    if (threeDsStatus === 'START_BACKGROUND_POLL') {
      setOrderStatusPromisePollerTimeout(1000, qrCode, setQrCode);
    }
    
    return Promise.resolve('3DS_REQUIRED');
  }
  
  if (status === 'SUCCESS') {
    const {paymentMethod} = orderJSON;
    
    if (paymentMethod === 'WECHATPAY' || paymentMethod === 'MODE' || paymentMethod === 'GOOGLEPAY'
      || getThreeDSVerificationProvider(state) === VerificationProvider.NUVEI) {
      const url = getSuccessRedirect(state) || '/checkout/complete';
      
      redirect.doRedirect(url);
      return Promise.resolve();
    }
    
    return Promise.resolve(orderJSON.orderCompleteRedirectUrl);
  }

  if (getHostedPaymentMethodTokenReceived(status)) {
    const redirectUrl = paymentAccessUrl;
    if (redirectUrl) {
      return Promise.resolve(redirectUrl);
    }
  } else if (status === 'WECHAT_PAYMENT_REQUESTED') {
    return WeChatHandler.handleWeChatPayment(orderJSON)
      .then(weChatResponse => processResponse(weChatResponse, qrCode, setQrCode));
  } else if (status === 'FAILURE') {
    const url = getErrorRedirect(state) || '/checkout';
    
    redirect.doRedirect(url);
  }
  
  return Promise.reject();
}

export default {
  callApiIfReturningFromHostedPaymentPage, processResponse,
};
